import { Signal } from '../utils/signals'
import WidgetCategory from '../components/WidgetCategory'
import z from 'zod'
import { NonEmptyList, Override, PartialDeep, Prettify } from '../utils/typescript'
import { widgetDefinitions } from '../components/widgets'

export type FeatureFlagsSchema = ReturnType<typeof buildSchema>
/** because we allow selectableWidgets to be faulty, see {@link parseFlag} */
export type FeatureFlagValues = Override<FeatureFlagValuesStrict, {selectableWidgets: PartialDeep<FeatureFlagValuesStrict['selectableWidgets']>}>
type FeatureFlagValuesStrict = Prettify<z.infer<FeatureFlagsSchema>>

export type FeatureFlags = {
  values: FeatureFlagValues
  changeSignal: Signal<FeatureFlagValues>
}

type FeatureFlagsBundle = {
  schema: FeatureFlagsSchema
  keys: (keyof FeatureFlagValues)[]
}

/** made to avoid circular deps, which jest does not like */
export function getFeatureFlagKeys(): (keyof FeatureFlagValues)[] {
  return getBundle().keys
}

export function parseFlag<K extends keyof FeatureFlagValues>(flag: K, value: unknown, onError?: (error: unknown) => void): FeatureFlagValues[K] {
  try {
    return getFeatureFlagsSchema().shape[flag].parse(value) as FeatureFlagValues[K]
  } catch(e) {
    const newError = new Error(`error whilst parsing flag "${flag}"\n\n` + e)
    onError?.(newError)

    if (flag === 'selectableWidgets' && value != null) {
      console.error("featureflag selectableWidgets was wrongly configured, applying it still\n\n" + newError)
      return value as FeatureFlagValues[K]
    }

    throw newError
  }
}

function getFeatureFlagsSchema(): FeatureFlagsSchema {
  return getBundle().schema
}

let LAZY_FEATURE_FLAGS: FeatureFlagsBundle
/** made to avoid circular deps, which jest does not like */
function getBundle(): FeatureFlagsBundle {
  if (LAZY_FEATURE_FLAGS != null) return LAZY_FEATURE_FLAGS

  const schema = buildSchema()
  LAZY_FEATURE_FLAGS = {
    schema,
    keys: Object.keys(schema.shape) as (keyof FeatureFlagValues)[],
  }
  return LAZY_FEATURE_FLAGS
}

/** built lazily to avoid circular deps, which jest does not like */
function buildSchema() {
  const names = widgetDefinitions.map(w => w.name)
  if (names.length === 0) throw new Error('widgetDefinitions empty — load-order cycle back')

  const namesSchema = z.array(z.enum(names as NonEmptyList<string>))
  const categoriesSchema = z.array(z.enum(Object.values(WidgetCategory) as NonEmptyList<string>))
  return z.object({
    singleLogin: z.boolean(),
    localMode: z.boolean(),
    /*
     * Inspector Gadget, the full Code Widget report in the app topbar: every observation and the page
     * source, next to the Lock&Publish check. On for the internal configurator platform
     * (bim-connected-platform), off elsewhere unless switched on per browser:
     * featureFlags.inspectorGadget = true
     */
    inspectorGadget: z.boolean(),
    selectableWidgets: z.object({
      names: z.object({
        allowed: namesSchema,
        blocked: namesSchema,
      }),
      categories: z.object({
        allowed: categoriesSchema,
        blocked: categoriesSchema,
      }),
      defaultPolicy: z.enum(['allowed', 'blocked']),
    }),
    codeWidget: z.object({
      enabled: z.boolean(),
      crossOriginRepoUrlAllowed: z.boolean(),
    }),
    /*
     * The AI agent chat. Off unless a deployment turns it on, like codeWidget — and for a
     * stronger reason: an enabled agent spends GPU time on the platform's own model host, so a
     * deployment that has not been given one must not offer it. `rest` enforces the same thing
     * independently (agent.allowed-repos); this flag is what stops the widget being offered and
     * rendered, not what makes it safe.
     */
    agentChat: z.object({
      enabled: z.boolean(),
    })
  })
}
